/**
 * MenuScene — 카와이 파스텔 스타일 메인 메뉴
 */
import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '../config';

export class MenuScene extends Phaser.Scene {
  private titleText!: Phaser.GameObjects.Text;
  private subtitleText!: Phaser.GameObjects.Text;

  constructor() {
    super({ key: 'MenuScene' });
  }

  create(): void {
    // ── 배경 이미지 ──
    const bg = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT / 2, 'bg_space');
    bg.setDisplaySize(GAME_WIDTH, GAME_HEIGHT);

    // ── 반투명 오버레이 (약간 밝게) ──
    const overlay = this.add.graphics();
    overlay.fillStyle(0xFFFFFF, 0.08);
    overlay.fillRect(0, 0, GAME_WIDTH, GAME_HEIGHT);

    // ── 떠다니는 장식 구름 ──
    this.createFloatingCloud(50, 120, 0.3, 3500);
    this.createFloatingCloud(280, 200, 0.25, 4200);
    this.createFloatingCloud(40, 450, 0.2, 3800);
    this.createFloatingCloud(310, 600, 0.22, 4500);
    this.createFloatingCloud(160, 750, 0.18, 5000);

    // ── 떠다니는 파티클 (별 반짝임) ──
    for (let i = 0; i < 25; i++) {
      const x = Phaser.Math.Between(10, GAME_WIDTH - 10);
      const y = Phaser.Math.Between(10, GAME_HEIGHT - 10);
      const s = this.add.circle(x, y, Phaser.Math.Between(1, 3), 0xFFFFFF, 0.6);

      this.tweens.add({
        targets: s,
        alpha: { from: 0.2, to: 0.8 },
        scale: { from: 0.5, to: 1.2 },
        duration: Phaser.Math.Between(1500, 3000),
        yoyo: true,
        repeat: -1,
        delay: Phaser.Math.Between(0, 2000),
      });
    }

    // ── 캐릭터 미리보기 ──
    const character = this.add.image(GAME_WIDTH / 2, GAME_HEIGHT * 0.28, 'player');
    character.setScale(0.25);
    character.setDepth(5);

    this.tweens.add({
      targets: character,
      y: character.y - 12,
      duration: 2000,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });

    // ── 타이틀 배경 버블 ──
    const titleBubble = this.add.graphics();
    titleBubble.fillStyle(0xFFFFFF, 0.25);
    titleBubble.fillRoundedRect(GAME_WIDTH / 2 - 160, GAME_HEIGHT * 0.39, 320, 100, 20);
    titleBubble.setDepth(4);

    // ── 타이틀 텍스트 ──
    this.titleText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT * 0.42, 'SKY WALKER', {
      fontFamily: '"Noto Sans KR", "Outfit", sans-serif',
      fontSize: '36px',
      fontStyle: 'bold',
      color: '#FFFFFF',
      stroke: '#9966CC',
      strokeThickness: 4,
      shadow: { offsetX: 0, offsetY: 3, color: '#00000033', blur: 8, fill: true, stroke: true },
    }).setOrigin(0.5).setDepth(5);

    this.subtitleText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT * 0.46 + 4, '하늘끝까지', {
      fontFamily: '"Noto Sans KR", sans-serif',
      fontSize: '16px',
      color: '#FFE4F0',
      stroke: '#9966CC',
      strokeThickness: 1,
    }).setOrigin(0.5).setDepth(5);

    // ── 별 장식 (타이틀 주변) ──
    const starPositions = [
      { x: GAME_WIDTH / 2 - 130, y: GAME_HEIGHT * 0.42 },
      { x: GAME_WIDTH / 2 + 130, y: GAME_HEIGHT * 0.42 },
      { x: GAME_WIDTH / 2 - 90, y: GAME_HEIGHT * 0.39 },
      { x: GAME_WIDTH / 2 + 90, y: GAME_HEIGHT * 0.39 },
    ];
    for (const pos of starPositions) {
      const star = this.add.text(pos.x, pos.y, '✦', {
        fontSize: '14px',
        color: '#FFE4B5',
      }).setOrigin(0.5).setDepth(5);

      this.tweens.add({
        targets: star,
        alpha: { from: 0.4, to: 1 },
        scale: { from: 0.8, to: 1.3 },
        duration: 1200,
        yoyo: true,
        repeat: -1,
        delay: Math.random() * 1000,
      });
    }

    // ── 메뉴 버튼 ──
    const btnStartY = GAME_HEIGHT * 0.56;
    this.createKawaiiButton(btnStartY, '▶  START', 0xAA77DD, 0xCC99EE, () => {
      this.cameras.main.fadeOut(500, 0, 0, 0);
      this.time.delayedCall(500, () => {
        this.scene.start('GameScene');
      });
    });

    this.createKawaiiButton(btnStartY + 70, '🏆  RANKING', 0x77AADD, 0x99CCEE, () => {
      this.scene.start('RankingScene');
    });

    this.createKawaiiButton(btnStartY + 140, '⚙️  SETTINGS', 0x88AACC, 0xAABBDD, () => {
      // TODO: SettingsScene
    });

    // ── 최고 기록 ──
    const bestScore = parseInt(localStorage.getItem('tothemoon_best') || '0', 10);

    const bestBubble = this.add.graphics();
    bestBubble.fillStyle(0xFFFFFF, 0.15);
    bestBubble.fillRoundedRect(GAME_WIDTH / 2 - 80, GAME_HEIGHT - 90, 160, 36, 18);

    this.add.text(GAME_WIDTH / 2, GAME_HEIGHT - 72, `★ Best: ${bestScore.toLocaleString()}`, {
      fontFamily: '"Noto Sans KR", sans-serif',
      fontSize: '16px',
      fontStyle: 'bold',
      color: '#FFD700',
      stroke: '#000000',
      strokeThickness: 2,
    }).setOrigin(0.5);

    // ── 하단 좌/우 터치 힌트 ──
    this.add.text(30, GAME_HEIGHT - 40, 'LEFT', {
      fontFamily: '"Noto Sans KR", sans-serif',
      fontSize: '12px',
      color: '#FFFFFF66',
    });
    this.add.text(GAME_WIDTH - 30, GAME_HEIGHT - 40, 'RIGHT', {
      fontFamily: '"Noto Sans KR", sans-serif',
      fontSize: '12px',
      color: '#FFFFFF66',
    }).setOrigin(1, 0);

    // ── 카메라 페이드 인 ──
    this.cameras.main.fadeIn(600, 0, 0, 0);
  }

  private createKawaiiButton(y: number, label: string, baseColor: number, highlightColor: number, callback: () => void): void {
    const btnW = 220;
    const btnH = 52;
    const x = GAME_WIDTH / 2;

    const bg = this.add.graphics();

    // 글래스모피즘 스타일 배경
    bg.fillStyle(0x000000, 0.15);
    bg.fillRoundedRect(x - btnW / 2 + 2, y + 3, btnW, btnH, 16);

    bg.fillStyle(baseColor, 0.6);
    bg.fillRoundedRect(x - btnW / 2, y, btnW, btnH, 16);

    // 상단 하이라이트
    bg.fillStyle(0xFFFFFF, 0.2);
    bg.fillRoundedRect(x - btnW / 2 + 3, y + 2, btnW - 6, btnH / 2 - 2, { tl: 14, tr: 14, bl: 0, br: 0 });

    // 테두리
    bg.lineStyle(1.5, 0xFFFFFF, 0.35);
    bg.strokeRoundedRect(x - btnW / 2, y, btnW, btnH, 16);

    const text = this.add.text(x, y + btnH / 2, label, {
      fontFamily: '"Noto Sans KR", "Outfit", sans-serif',
      fontSize: '20px',
      fontStyle: 'bold',
      color: '#FFFFFF',
      stroke: '#00000022',
      strokeThickness: 1,
    }).setOrigin(0.5);

    // 터치 영역
    const hitArea = this.add.rectangle(x, y + btnH / 2, btnW, btnH).setInteractive({ useHandCursor: true });
    hitArea.setAlpha(0.001);

    hitArea.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(0x000000, 0.15);
      bg.fillRoundedRect(x - btnW / 2 + 2, y + 3, btnW, btnH, 16);
      bg.fillStyle(highlightColor, 0.7);
      bg.fillRoundedRect(x - btnW / 2, y, btnW, btnH, 16);
      bg.fillStyle(0xFFFFFF, 0.25);
      bg.fillRoundedRect(x - btnW / 2 + 3, y + 2, btnW - 6, btnH / 2 - 2, { tl: 14, tr: 14, bl: 0, br: 0 });
      bg.lineStyle(2, 0xFFFFFF, 0.5);
      bg.strokeRoundedRect(x - btnW / 2, y, btnW, btnH, 16);
    });

    hitArea.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(0x000000, 0.15);
      bg.fillRoundedRect(x - btnW / 2 + 2, y + 3, btnW, btnH, 16);
      bg.fillStyle(baseColor, 0.6);
      bg.fillRoundedRect(x - btnW / 2, y, btnW, btnH, 16);
      bg.fillStyle(0xFFFFFF, 0.2);
      bg.fillRoundedRect(x - btnW / 2 + 3, y + 2, btnW - 6, btnH / 2 - 2, { tl: 14, tr: 14, bl: 0, br: 0 });
      bg.lineStyle(1.5, 0xFFFFFF, 0.35);
      bg.strokeRoundedRect(x - btnW / 2, y, btnW, btnH, 16);
    });

    hitArea.on('pointerdown', () => {
      this.tweens.add({
        targets: [text],
        scaleX: 0.92,
        scaleY: 0.92,
        duration: 80,
        yoyo: true,
        onComplete: callback,
      });
    });
  }

  private createFloatingCloud(x: number, y: number, scale: number, duration: number): void {
    const cloud = this.add.image(x, y, 'cloud_normal');
    cloud.setScale(scale);
    cloud.setAlpha(0.35);
    cloud.setDepth(1);

    this.tweens.add({
      targets: cloud,
      x: x + 30,
      y: y - 8,
      duration,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }
}
